'use client';

import * as React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
} from '@src/v3/components/coreUI/dialog';
import Button from '@src/v3/components/coreUI/button';
import TextField from '@src/v3/components/coreUI/textField';
import { cn } from '@src/lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { getModalHeight } from '@src/v3/utils/helper';
import Icon from '../coreUI/icon';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onOpenChange:
    | React.Dispatch<React.SetStateAction<boolean>>
    // eslint-disable-next-line no-unused-vars
    | ((open: boolean) => void);
  title: string;
  showCloseButton?: boolean;
  variant?: 'primary' | 'danger' | 'success' | 'warning' | 'info';
  message: string | React.ReactNode;
  primaryButtonText: string;
  secondaryButtonText?: string;
  onPrimaryAction: () => void;
  onSecondaryAction?: () => void;
  isLoading?: boolean;
  className?: string;
  showOtpField?: boolean;
  otpValue?: string;
  // eslint-disable-next-line no-unused-vars
  onOtpChange?: (value: string) => void;
  phoneResendTimer?: number;
  canResendPhone?: boolean;
  onPhoneResend?: () => void;
  primaryButtonClassName?: string;
  primaryButtonDisabled?: boolean;
  secondaryButtonClassName?: string;
  secondaryButtonDisabled?: boolean;
  children?: React.ReactNode;
  contentClassName?: string;
  hideFooter?: boolean;
  titleClassName?: string;
  messageClassName?: string;
  overlayClassName?: string;
  mobileFooterClassName?: string;
  /** Content rendered above the footer buttons (not scrollable) */
  footerContent?: React.ReactNode;
  mobileContentClassName?: string;
  crossIconClassName?: string;
}

export function ConfirmationModal({
  isOpen,
  onOpenChange,
  variant = 'primary',
  showCloseButton = true,
  title,
  message,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryAction,
  onSecondaryAction,
  isLoading = false,
  className,
  showOtpField = false,
  otpValue = '',
  onOtpChange,
  phoneResendTimer = 0,
  canResendPhone = false,
  onPhoneResend,
  primaryButtonClassName,
  primaryButtonDisabled = false,
  secondaryButtonClassName,
  secondaryButtonDisabled = false,
  children,
  contentClassName,
  hideFooter = false,
  titleClassName,
  messageClassName,
  overlayClassName,
  mobileFooterClassName,
  mobileContentClassName,
  footerContent,
  crossIconClassName,
}: ConfirmationModalProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Get button styles based on variant
  const getButtonStyles = () => {
    switch (variant) {
      case 'danger':
        return 'bg-red-700 text-white hover:bg-red-800';
      case 'success':
        return 'bg-green-600 text-white hover:bg-green-700';
      case 'warning':
        return 'bg-yellow-600 text-white hover:bg-yellow-700';
      case 'info':
        return 'bg-blue-600 text-white hover:bg-blue-700';
      default:
        return 'bg-focus text-white hover:bg-focus/90';
    }
  };

  // Format timer display as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Set up mobile detection with resize handling
  useEffect(() => {
    function handleResize() {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    }

    // Initial check
    handleResize();

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update isAnimating when isOpen changes
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isMobile, isOpen]);

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction();
    } else if (secondaryButtonText) {
      handleClose();
    }
  };

  // Mobile drawer implementation
  if (isMobile) {
    return createPortal(
      <AnimatePresence mode="wait" onExitComplete={() => setIsAnimating(false)}>
        {(isOpen || isAnimating) && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-[9999]"
              onClick={handleClose}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{
                type: 'spring',
                damping: 30,
                stiffness: 300,
                mass: 0.8,
                restDelta: 0.001,
                restSpeed: 0.001,
              }}
              className={cn(
                'fixed bottom-0 left-0 right-0 !w-full max-h-[90vh] p-0 rounded-t-xl rounded-b-none !max-w-none flex flex-col bg-background z-[10000] pointer-events-auto',
                className
              )}
              onAnimationStart={() => setIsAnimating(true)}
              onAnimationComplete={() => setIsAnimating(false)}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={cn(
                  'w-full flex flex-col py-8 px-5 gap-5 overflow-y-auto flex-1 min-h-0 pointer-events-auto',
                  mobileContentClassName
                )}
              >
                <div className="flex flex-row justify-between items-start">
                  <h2
                    className={cn(
                      'text-gray-1000 font-medium text-2xl',
                      titleClassName
                    )}
                  >
                    {title}
                  </h2>
                  {showCloseButton && (
                    <span
                      onClick={handleClose}
                      className={`${crossIconClassName}`}
                    >
                      <Icon
                        iconName="crossicon"
                        iconProps={{ className: 'w-5 h-5 text-gray-1000' }}
                      />
                    </span>
                  )}
                </div>
                {message && (
                  <div
                    className={cn(
                      'text-gray-800 font-normal text-lg leading-[28px]',
                      messageClassName
                    )}
                  >
                    {message}
                  </div>
                )}
                {showOtpField && (
                  <div className="mt-2">
                    <TextField
                      value={otpValue}
                      onChange={(e) => onOtpChange?.(e.target.value)}
                      name="otp"
                      size="lg"
                      variant="dark"
                      containerClassName="w-44"
                      maxLength={6}
                      className="border-gray-600 rounded-xl"
                    />
                    <div className="flex justify-between items-center mt-6">
                      <p className="text-gray-1000 font-medium text-base">
                        Didn&apos;t get it? Wait a moment, or <br />
                        <button
                          onClick={onPhoneResend}
                          disabled={!canResendPhone}
                          className={`${
                            canResendPhone
                              ? 'text-primary-dark underline'
                              : 'text-gray-800'
                          } font-medium`}
                        >
                          request a new code.
                        </button>
                      </p>
                      {phoneResendTimer > 0 && (
                        <p className="text-gray-700 font-medium text-base">
                          {formatTime(phoneResendTimer)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {children && (
                  <div
                    className={cn(
                      'w-full pointer-events-auto !z-[99]',
                      contentClassName
                    )}
                  >
                    {children}
                  </div>
                )}
              </div>

              {/* Footer Content - rendered above buttons */}
              {footerContent && (
                <div className="w-full px-6 flex-shrink-0 pointer-events-auto">
                  {footerContent}
                </div>
              )}

              {!hideFooter && (
                <div
                  className={cn(
                    mobileFooterClassName,
                    'flex w-full flex-col gap-y-3 gap-x-5 px-6 pt-8 pb-5 flex-shrink-0 pointer-events-auto'
                  )}
                >
                  <Button
                    onClick={onPrimaryAction}
                    isLoading={isLoading}
                    disabled={primaryButtonDisabled || isLoading}
                    className={cn(
                      'flex-1 h-12 min-h-[48px] font-semibold text-base rounded-xl',
                      getButtonStyles(),
                      primaryButtonClassName
                    )}
                  >
                    {primaryButtonText}
                  </Button>

                  {secondaryButtonText ? (
                    <Button
                      onClick={handleSecondaryAction}
                      disabled={secondaryButtonDisabled || isLoading}
                      className={cn(
                        'flex-1 h-12 min-h-[48px] bg-gray-200 font-semibold text-base rounded-xl text-gray-1000 hover:bg-gray-300',
                        secondaryButtonClassName
                      )}
                    >
                      {secondaryButtonText}
                    </Button>
                  ) : null}
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogOverlay
        className={cn(
          'sm:backdrop-blur-[15px] bg-[rgba(0,0,0,0.21)]',
          overlayClassName
        )}
      />
      <DialogContent
        showCloseButton={showCloseButton}
        size="sm"
        className={cn(
          `!w-[496px] !max-w-[90vw] max-h-[90vh] !p-6 flex flex-col gap-6 rounded-3xl bg-background shadow-[0_317px_89px_0_rgba(0,0,0,0.00),_0_203px_81px_0_rgba(0,0,0,0.01),_0_114px_68px_0_rgba(0,0,0,0.03),_0_51px_51px_0_rgba(0,0,0,0.04),_0_13px_28px_0_rgba(0,0,0,0.05)] ${getModalHeight(
            showOtpField,
            children
          )}`,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={cn(
            'w-full flex flex-col flex-1 min-h-0',
            children ? 'gap-4' : 'gap-4'
          )}
        >
          <DialogHeader>
            <DialogTitle
              className={cn(
                '!text-gray-1000 font-medium text-2xl',
                titleClassName
              )}
            >
              {title}
            </DialogTitle>
          </DialogHeader>
          {message && (
            <DialogDescription
              className={cn(
                '!text-gray-800 font-normal text-lg leading-[30px]',
                messageClassName
              )}
            >
              {message}
            </DialogDescription>
          )}
          {showOtpField && (
            <div className="mt-2">
              <TextField
                value={otpValue}
                onChange={(e) => onOtpChange?.(e.target.value)}
                name="otp"
                size="lg"
                variant="dark"
                containerClassName="w-44"
                maxLength={6}
                className="border-gray-600 rounded-xl max-w-[186px] text-center w-full"
              />
              <div className="flex justify-between max-w-[360px] w-full items-center mt-6">
                <p className="text-gray-1000 font-medium text-base">
                  Didn&apos;t get it? Wait a moment, or <br />
                  <button
                    onClick={onPhoneResend}
                    disabled={!canResendPhone}
                    className={`${
                      canResendPhone
                        ? 'text-primary-dark underline'
                        : 'text-gray-800'
                    } font-medium`}
                  >
                    request a new code.
                  </button>
                </p>
                {phoneResendTimer > 0 && (
                  <p className="text-gray-700 font-medium text-base">
                    {formatTime(phoneResendTimer)}
                  </p>
                )}
              </div>
            </div>
          )}
          {children && (
            <div className={cn('w-full overflow-y-auto', contentClassName)}>
              {children}
            </div>
          )}
        </div>

        {/* Footer Content - rendered above buttons */}
        {footerContent && <div className="w-full">{footerContent}</div>}

        {!hideFooter && (
          <DialogFooter className="flex w-full gap-5 sm:flex-row flex-col">
            {secondaryButtonText ? (
              <Button
                onClick={handleSecondaryAction}
                disabled={secondaryButtonDisabled}
                className={cn(
                  'flex-1 h-10 min-h-[48px] bg-gray-200 font-semibold text-base rounded-xl text-gray-1000 hover:bg-gray-300',
                  secondaryButtonClassName
                )}
              >
                {secondaryButtonText}
              </Button>
            ) : null}

            <Button
              onClick={onPrimaryAction}
              isLoading={isLoading}
              disabled={primaryButtonDisabled || isLoading}
              className={cn(
                'flex-1 h-10 min-h-[48px] font-semibold text-base rounded-xl',
                getButtonStyles(),
                primaryButtonClassName
              )}
            >
              {primaryButtonText}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmationModal;
